/**
 * The element every Kola screen lives in — `.kola-root` — and the one thing
 * that makes Kola safe to mount inside another app's document (2026-09):
 * every selector in app.css is scoped under it at build time (postcss-
 * scope.mjs) and the design tokens are declared on it (tokens.css), so Kola
 * styles nothing outside its own box and inherits nothing it did not ask for.
 *
 * Two elements, in fact. Menus that must escape the transformed camera layer
 * portal to `document.body` (features/board/ContextMenu.tsx); outside
 * `.kola-root` they would lose every `--color-*` token. So the root also owns
 * a zero-size, fixed-position PORTAL LAYER on <body> that carries the same
 * class and theme stamp, and hands it out through `useKolaPortalTarget()`.
 * Both are registered with core/theme.ts, which stamps `data-theme` on them.
 */
import { createContext, useContext, useLayoutEffect, useRef, useState, type ReactNode } from "react";

import { registerThemeRoot } from "../core/theme.js";

const PortalTargetContext = createContext<HTMLElement | null>(null);

/** Where a `document.body` portal should render instead, tokens included. */
export function useKolaPortalTarget(): HTMLElement {
  return useContext(PortalTargetContext) ?? document.body;
}

export interface KolaRootProps {
  children: ReactNode;
}

export function KolaRoot({ children }: KolaRootProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [portalLayer] = useState(() => {
    const element = document.createElement("div");
    element.className = "kola-root kola-portal-layer";
    return element;
  });

  // Layout effect, not effect: the stamp must be on the element before the
  // first paint or the light tokens flash for a frame under a dark theme.
  useLayoutEffect(() => {
    const element = ref.current;
    if (element === null) return;
    const unregisterRoot = registerThemeRoot(element);
    document.body.appendChild(portalLayer);
    const unregisterLayer = registerThemeRoot(portalLayer);
    return () => {
      unregisterRoot();
      unregisterLayer();
      portalLayer.remove();
    };
  }, [portalLayer]);

  return (
    <PortalTargetContext.Provider value={portalLayer}>
      <div ref={ref} className="kola-root">
        {children}
      </div>
    </PortalTargetContext.Provider>
  );
}
